import React, { useEffect, useState } from 'react';
import {
  AgenteHorario,
  AttendanceCalcResponse,
  AttendanceEstado,
  exportAsistenciaCiclosCsv,
  getAsistenciaCiclos,
  getAsistenciaCiclosAgentes,
  getAsistenciaCiclosCatalog,
} from '../services/horarios.service';
import { getErrorMessage } from '../utils/errorHandler';
import { formatTime24 } from '../utils/dateTime';

const toIsoDate = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const getTodayIso = (): string => toIsoDate(new Date());

const getCurrentWeekStartIso = (): string => {
  const now = new Date();
  const mondayOffset = (now.getDay() + 6) % 7;
  const monday = new Date(now);
  monday.setDate(now.getDate() - mondayOffset);
  return toIsoDate(monday);
};

const estados: AttendanceEstado[] = ['TODOS', 'OK', 'OBSERVADO', 'AUSENTE', 'JUSTIFICADO', 'FERIADO'];

const AsistenciaCalculoPage: React.FC = () => {
  const [agentes, setAgentes] = useState<AgenteHorario[]>([]);
  const [ciclos, setCiclos] = useState<Array<{ _id: string; nombre: string }>>([]);
  const [agenteId, setAgenteId] = useState('');
  const [cicloId, setCicloId] = useState('');
  const [estado, setEstado] = useState<AttendanceEstado>('TODOS');
  const [desde, setDesde] = useState<string>(() => getCurrentWeekStartIso());
  const [hasta, setHasta] = useState<string>(() => getTodayIso());
  const [data, setData] = useState<AttendanceCalcResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    void Promise.all([getAsistenciaCiclosAgentes(), getAsistenciaCiclosCatalog()])
      .then(([agentesResp, ciclosResp]) => {
        const agentesData = Array.isArray(agentesResp.data) ? agentesResp.data : [];
        const ciclosData = Array.isArray(ciclosResp.data) ? ciclosResp.data : [];
        setAgentes(agentesData);
        setCiclos(ciclosData);
        console.info('[asistencia-calculo] catalogos cargados', {
          agentesConCiclo: agentesData.length,
          ciclosDisponibles: ciclosData.length,
        });
      })
      .catch((err) => {
        console.error('[asistencia-calculo] error cargando catalogos', err);
        setError(getErrorMessage(err));
      });
  }, []);

  const generar = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    setData(null);

    try {
      console.info('[asistencia-calculo] solicitando reporte', {
        desde,
        hasta,
        agenteId: agenteId || 'ALL',
        cicloId: cicloId || 'ALL',
        estado,
      });
      const resp = await getAsistenciaCiclos({
        desde,
        hasta,
        agenteId: agenteId || undefined,
        cicloId: cicloId || undefined,
        estado,
      });
      setData(resp.data);
      console.info('[asistencia-calculo] respuesta reporte', {
        totalFilas: resp.data?.meta?.totalFilas ?? 0,
      });
    } catch (err) {
      console.error('[asistencia-calculo] error generando reporte', err);
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const exportar = async () => {
    try {
      const blob = await exportAsistenciaCiclosCsv({
        desde,
        hasta,
        agenteId: agenteId || undefined,
        cicloId: cicloId || undefined,
        estado,
      });
      const href = URL.createObjectURL(blob.data);
      const a = document.createElement('a');
      a.href = href;
      a.download = `reporte-calculo-asistencia-${Date.now()}.csv`;
      a.click();
      URL.revokeObjectURL(href);
    } catch (err) {
      setError(getErrorMessage(err));
    }
  };

  return (
    <>
      <h1 className="page-title">Reporte Cálculo Asistencia</h1>

      <div className="card">
        <h2 className="card-title">Filtros</h2>
        {error && <div className="error-msg">{error}</div>}

        <form onSubmit={generar}>
          <div className="filters-grid" style={{ marginBottom: '12px' }}>
            <div className="form-group">
              <label>Desde</label>
              <input type="date" value={desde} onChange={(e) => setDesde(e.target.value)} required />
            </div>
            <div className="form-group">
              <label>Hasta</label>
              <input type="date" value={hasta} onChange={(e) => setHasta(e.target.value)} required />
            </div>
            <div className="form-group">
              <label>Legajo</label>
              <select value={agenteId} onChange={(e) => setAgenteId(e.target.value)}>
                <option value="">Todos</option>
                {agentes.map((agente) => (
                  <option key={agente._id} value={agente._id}>
                    {agente.nombre}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Ciclo</label>
              <select value={cicloId} onChange={(e) => setCicloId(e.target.value)}>
                <option value="">Todos</option>
                {ciclos.map((ciclo) => (
                  <option key={ciclo._id} value={ciclo._id}>
                    {ciclo.nombre}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Estado</label>
              <select value={estado} onChange={(e) => setEstado(e.target.value as AttendanceEstado)}>
                {estados.map((estadoItem) => (
                  <option key={estadoItem} value={estadoItem}>
                    {estadoItem}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="filter-actions">
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Generando...' : 'Generar reporte'}
            </button>
            <button
              type="button"
              className="btn btn-outline"
              onClick={exportar}
              disabled={loading || !data || data.rows.length === 0}
            >
              Exportar Excel
            </button>
          </div>
        </form>
      </div>

      {data && (
        <div className="card">
          <div className="form-grid" style={{ marginBottom: '16px' }}>
            <div><strong>Periodo:</strong> {data.meta.periodoDesde} a {data.meta.periodoHasta}</div>
            <div><strong>Total filas:</strong> {data.meta.totalFilas}</div>
          </div>

          {data.rows.length === 0 ? (
            <div className="empty-state">No hay datos para los filtros seleccionados.</div>
          ) : (
            <div className="table-wrapper">
              <table>
                <thead>
                  <tr>
                   
                    <th>Name</th>
                    <th>DNI</th>
                    <th>fecha</th>
                    <th>hora entrada Turno</th>
                    <th>hora salida Turno</th>
                    <th>hora entrada real</th>
                    <th>hora salida real</th>
                    <th>ausente</th>
                    <th>llegada Tarde</th>
                  {/* <th>Justificacion</th> */}
                    <th>Novedad</th>
                    <th>feriado</th>
                    <th>tipo feriado</th>
                    {/* <th>dia semana</th> */}
                   {/*  <th>Turno Nocturno</th>  */}
                    <th>Salida temprano</th>
                    {/* <th>minutos trabajados</th> */}
                    <th>Horas Trabajadas</th>
                    <th>Ciclo</th>
                    <th>Turno</th>
                    <th>Estado</th>
                  </tr>
                </thead>
                <tbody>
                  {data.rows.map((row, i) => (
                    <tr key={`${row.userid}-${row.fecha}-${i}`}>
                      
                      <td>{row.name}</td>
                      <td>{row.idcard}</td>
                      <td>{row.fecha}</td>
                      <td>{formatTime24(row.horaEntradaTurno)}</td>
                      <td>{formatTime24(row.horaSalidaTurno)}</td>
                      <td>{row.horaEntradaReal ? formatTime24(row.horaEntradaReal) : '-'}</td>
                      <td>{row.horaSalidaReal ? formatTime24(row.horaSalidaReal) : '-'}</td>
                      <td>{row.ausente}</td>
                      <td>{row.llegadaTarde}</td>
                    {/* <td>{row.justificacion}</td> */}
                      <td>{row.tipoJustificacion || '-'}</td>
                      <td>{row.feriado}</td>
                      <td>{row.tipoFeriado || '-'}</td>
                    {/* <td>{row.diaSemana}</td> */}
                    {/* <td>{row.turnoNocturno}</td> */}
                      <td>{row.salidaTemprano}</td>
                    {/* <td>{row.minutosTrabajados}</td> */}  
                      <td>{row.horasTrabajadas}</td>
                      <td>{row.ciclo}</td>
                      <td>{row.turno}</td>
                      <td>
                        <span
                          className={`badge ${
                            row.estado === 'OK'
                              ? 'badge-success'
                              : row.estado === 'AUSENTE'
                                ? 'badge-danger'
                                : row.estado === 'OBSERVADO'
                                  ? 'badge-warning'
                                  : 'badge-secondary'
                          }`}
                        >
                          {row.estado}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default AsistenciaCalculoPage;
